import { ImpressionsList } from '../types/types';
import { validateAsset } from '@dertour/common-lib';

interface FetchImpressionsParams {
    brand: string;
    tenant: string;
    currency: string;
    lang: string;
    identifier: string;
    intent?: string;
    dataSrc?: string;
}

interface ApiResponse {
    title?: string;
    supTitle?: string;
    intro?: string;
    items?: Array<{
        title: string;
        text: string;
        img: string;
    }>;
    meta?: {
        service: string;
        version: string;
        buildTime: string;
    };
    error?: string;
    missing?: string[];
    invalid?: string[];
}

/**
 * Service for loading impressions data via midlayer
 */
export class ImpressionsService {
    /**
     * Fetch impressions data from midlayer Azure Function
     * @param params - Request parameters including brand, tenant, currency, lang, identifier, intent, travelContext
     * @returns Promise with ImpressionsList
     */
    static async fetchImpressions(params: FetchImpressionsParams): Promise<ImpressionsList> {
        const { brand, tenant, currency, lang, identifier, intent } = params;
        const url = new URL(
            import.meta.env.VITE_MODE === 'DEV' ? `${import.meta.env.VITE_SERVICE_URL}` : `__SERVICE-URL__`
        );
        url.searchParams.set('brand', brand);
        url.searchParams.set('tenant', tenant);
        url.searchParams.set('currency', currency);
        url.searchParams.set('lang', lang);
        url.searchParams.set('identifier', identifier);
        url.searchParams.set('travelcontext', 'ROUNDTRIPS');
        if (intent) {
            url.searchParams.set('intent', intent);
        }

        try {
            const response = await fetch(url.toString());

            if (!response.ok) {
                // Try to parse error response
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData: ApiResponse = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                        if (errorData.missing && errorData.missing.length > 0) {
                            errorMessage += ` (Missing parameters: ${errorData.missing.join(', ')})`;
                        }
                        if (errorData.invalid && errorData.invalid.length > 0) {
                            errorMessage += ` (Invalid parameters: ${errorData.invalid.join(', ')})`;
                        }
                    }
                } catch {
                    // Ignore JSON parse errors for error response
                }
                throw new Error(errorMessage);
            }

            const data: ApiResponse = await response.json();

            // Validate data
            if (!data.items || data.items.length === 0) {
                throw new Error('impressionsList is missing or impressionsList.items is empty.');
            }

            // Warn if there are 2 or fewer items (component requires at least 3)
            if (data.items.length <= 2) {
                console.warn(
                    `[ImpressionsService] Component requires at least 3 items to display. Found ${data.items.length} item(s).`
                );
            }

            const validationResults = await Promise.all(
                // validate images and preload first image only
                data.items.map((item, index) => validateAsset(item.img, index === 0))
            );
            const validatedItems = data.items.filter((_, index) => Boolean(validationResults[index]));

            return {
                title: data.title || '',
                supTitle: data.supTitle || '',
                intro: data.intro || '',
                items: validatedItems,
            };
        } catch (error) {
            console.error('❌ [ImpressionsService] Error:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Failed to fetch impressions data');
        }
    }
}
